import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventService } from '../services/api.service';
import { FaSearch, FaSpinner, FaMapMarkerAlt } from 'react-icons/fa';
import './SearchBar.css';

const SearchBar = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const searchTimeout = useRef(null);
    const suggestionsRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
                setSuggestions([]);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Simple Turkish character normalization
    const normalizeTurkishText = (text) => {
        return text.toLowerCase()
            .replace(/İ/g, 'i')
            .replace(/I/g, 'i')
            .replace(/ı/g, 'i');
    };

    const handleSearch = async (value) => {
        setQuery(value);
        setSelectedIndex(-1);

        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        if (value.length >= 2) {
            setLoading(true);
            searchTimeout.current = setTimeout(async () => {
                try {
                    // First try title search
                    let response = await eventService.getAllEvents({ search: value });
                    console.log('Title search response:', response);

                    // If no results from title search, try venue search
                    if (!response || !Array.isArray(response) || response.length === 0) {
                        response = await eventService.getAllEvents({ venue: value });
                        console.log('Venue search response:', response);
                    }

                    if (response && Array.isArray(response)) {
                        const searchTerm = value.toLowerCase();
                        const validEvents = response.filter(event => {
                            if (!event || !event._id) return false;

                            const titleMatch = event.title.toLowerCase().includes(searchTerm);
                            const venueMatch = event.venues?.some(venue => 
                                venue.toLowerCase().includes(searchTerm)
                            );

                            // Only return events that actually match the search term
                            return titleMatch || venueMatch;
                        });

                        console.log('Filtered events:', validEvents);
                        setSuggestions(validEvents);
                    } else {
                        setSuggestions([]);
                    }
                } catch (error) {
                    console.error('Error fetching suggestions:', error);
                    setSuggestions([]);
                } finally {
                    setLoading(false);
                }
            }, 300);
        } else {
            setSuggestions([]);
        }
    };

    const handleKeyDown = (e) => {
        if (suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0) {
                    handleSuggestionClick(suggestions[selectedIndex]);
                } else if (query.trim()) {
                    handleSubmit(e);
                }
                break;
            case 'Escape':
                setSuggestions([]);
                setSelectedIndex(-1);
                break;
            default:
                break;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (query.trim()) {
            navigate(`/search?q=${encodeURIComponent(query.trim())}`);
            setSuggestions([]);
            setQuery('');
        }
    };

    const handleSuggestionClick = (event) => {
        if (!event || !event._id) {
            console.error('Invalid event data:', event);
            return;
        }
        navigate(`/event/${event._id}`);
        setSuggestions([]);
        setQuery('');
    };

    return (
        <div className="search-container" ref={suggestionsRef}>
            <form onSubmit={handleSubmit} className="search-form">
                <div className="search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => handleSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Etkinlik ara..."
                        className="search-input"
                        autoComplete="off"
                    />
                    {loading && <FaSpinner className="loading-icon spinning" />}
                </div>
                <button type="submit" className="search-button">
                    Ara
                </button>
            </form>

            {suggestions.length > 0 && (
                <div className="search-suggestions">
                    {suggestions.map((event, index) => (
                        event && event._id ? (
                            <div
                                key={event._id}
                                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                                onClick={() => handleSuggestionClick(event)}
                                onMouseEnter={() => setSelectedIndex(index)}
                            >
                                <img 
                                    src={event.image_url || '/default-event-image.jpg'} 
                                    alt={event.title || 'Event'} 
                                    className="suggestion-image"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/default-event-image.jpg';
                                    }}
                                    loading="lazy"
                                />
                                <div className="suggestion-info">
                                    <span className="suggestion-title">{event.title || 'Untitled Event'}</span>
                                    {event.venues && event.venues.length > 0 && (
                                        <div className="suggestion-venues">
                                            <FaMapMarkerAlt className="venue-icon" />
                                            <span className="venue-list">
                                                {event.venues.join(' • ')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null
                    ))}
                </div>
            )}
        </div>
    );
};

export default SearchBar; 